import { toast } from 'react-toastify';
import { CreateInvoiceDTO, InvoiceHistoryField } from './invoice.type';
import { format, formatISODate } from '../../common/utils/common.util';
import { ReportInvoiceDTO } from '../monitoring-reports/shared';
import { createInvoicesSummaryContent } from '../../common/pdfmake/InvoiceSummaryContent';

export interface ProjectDataForExport {
  projectNumber?: string;
  projectTitle?: string;
  clientName?: string;
  clientAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  projectAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  loanOfficerName?: string;
  siteVisitNumber?: string;
}

export const INVOICE_HISTORY_FIELD_LABELS: Record<InvoiceHistoryField, string> = {
  Number: 'Invoice #',
  InvoiceDate: 'Date',
  InvoiceAmount: 'Amount',
  PaidAmount: 'Amount Paid',
  Comment: 'Comment',
};

/** Formats a raw invoice history value (as stored by the backend) for display. */
export const formatInvoiceHistoryValue = (field: InvoiceHistoryField, value?: string | null): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'InvoiceAmount' || field === 'PaidAmount') return format(value, 'currency');
  if (field === 'InvoiceDate') return formatISODate(value) || value;
  return value;
};

const formatFilenameDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length !== 3) return '';
  return `${parts[1]}-${parts[2]}-${parts[0].slice(-2)}`;
};

export const extractInvoiceSuffix = (number: string): number | null => {
  const match = number?.match(/-(\d+(?:\.\d+)?)$/);
  return match ? parseFloat(match[1]) : null;
};

// Sort invoices by invoice number suffix (desc), falling back to createdOn date (desc)
export const sortInvoicesByNumberDesc = <T extends { invoice: { number: string }; createdOn?: string }>(invoices: T[]): T[] =>
  [...invoices].sort((a, b) => {
    const numA = extractInvoiceSuffix(a.invoice.number);
    const numB = extractInvoiceSuffix(b.invoice.number);
    if (numA !== null && numB !== null) return numB - numA;
    if (numA !== null) return -1;
    if (numB !== null) return 1;
    const dateA = new Date(a.createdOn || '').getTime();
    const dateB = new Date(b.createdOn || '').getTime();
    return dateB - dateA;
  });

export const exportInvoiceAsPDF = async (
  invoiceData: CreateInvoiceDTO,
  projectData: ProjectDataForExport,
  projectIdentifier?: string,
  suppressToast?: boolean,
) => {
  try {
    // Import pdfmake dynamically
    const pdfMake = await import('pdfmake/build/pdfmake.min');
    window.pdfMake = pdfMake;

    // Import the PDF creation function
    const { createInvoicePDF } = await import('../../common/pdfmake/InvoicePDF');

    // Import font setup utility
    const { setupFontsForPdfMake } = await import('../../common/pdfmake/pdfmake.util');

    // Setup fonts for pdfmake
    setupFontsForPdfMake();

    // Generate PDF definition
    const pdfDefinition = createInvoicePDF({
      invoiceData,
      loanOfficerName: projectData.loanOfficerName,
      siteVisitNumber: projectData.siteVisitNumber,
    });

    // Generate PDF and download
    const projName = projectData.projectTitle || projectIdentifier || 'Project';
    const reportDate = formatFilenameDate(invoiceData.invoice.invoiceDate);
    const reportNum = projectData.siteVisitNumber || '';
    const fileName = `CDCC INVOICE ${projName}${reportDate ? ' ' + reportDate : ''}${reportNum ? ' Report ' + reportNum : ''}.pdf`;

    pdfMake.createPdf(pdfDefinition as any).download(fileName);

    if (!suppressToast) {
      toast.success('Invoice PDF exported successfully!');
    }
  } catch (error) {
    console.error('PDF generation error:', error);
    toast.error('Failed to generate PDF');
  }
};

export const exportAllInvoicesAsPDF = async (
  invoices: ReportInvoiceDTO[],
  projectData: ProjectDataForExport,
  projectIdentifier?: string,
  suppressToast?: boolean,
) => {
  try {
    // Import pdfmake dynamically
    const pdfMake = await import('pdfmake/build/pdfmake.min');
    window.pdfMake = pdfMake;

    // Import font setup utility
    const { setupFontsForPdfMake } = await import('../../common/pdfmake/pdfmake.util');

    // Setup fonts for pdfmake
    setupFontsForPdfMake();

    // Separate current and past invoices
    const currentInvoice = invoices.find(invoice => invoice.currentInvoice);
    const pastInvoices = sortInvoicesByNumberDesc(invoices.filter(invoice => !invoice.currentInvoice));

    if (!currentInvoice) {
      toast.error('Current invoice is required for export');
      return;
    }

    // Import the PDF creation function
    const { createInvoicePDF } = await import('../../common/pdfmake/InvoicePDF');

    // Create current invoice page
    const currentInvoiceData: CreateInvoiceDTO = {
      invoice: {
        number: currentInvoice.invoice.number,
        invoiceDate: currentInvoice.invoice.invoiceDate,
        invoiceAmount: currentInvoice.invoice.invoiceAmount,
        paidAmount: currentInvoice.invoice.paidAmount,
        comment: currentInvoice.invoice.comment,
      },
      recepient: currentInvoice.recepient,
      recepientAddress: currentInvoice.recepientAddress,
      projectName: currentInvoice.projectName,
      projectAddress: currentInvoice.projectAddress,
      service: currentInvoice.service,
    };

    const currentInvoicePDF = createInvoicePDF({
      invoiceData: currentInvoiceData,
      loanOfficerName: projectData.loanOfficerName,
      siteVisitNumber: projectData.siteVisitNumber,
    });

    // Create past invoices page (if any exist)
    const invoicesSummaryContent = createInvoicesSummaryContent(currentInvoice as any, pastInvoices as any, currentInvoiceData);

    // Combine both pages
    const pdfDefinition = {
      content: [...currentInvoicePDF.content, { text: '', pageBreak: 'before' }, ...invoicesSummaryContent],
      footer: currentInvoicePDF.footer,
      styles: {
        ...currentInvoicePDF.styles,
        sectionHeader: {
          fontSize: 16,
          bold: true,
          color: '#1976d2',
          decoration: 'underline',
        },
        tableHeader: {
          margin: [0, 0, 0, 10],
          fontSize: 10,
          bold: true,
          color: '#333333',
        },
        tableFooter: {
          margin: [0, 10, 0, 0],
          fontSize: 10,
          bold: true,
          color: '#333333',
        },
        tableCell: {
          fontSize: 10,
          color: '#333333',
        },
      },
      defaultStyle: currentInvoicePDF.defaultStyle,
      pageSize: currentInvoicePDF.pageSize,
      pageMargins: currentInvoicePDF.pageMargins,
    };

    // Generate filename based on current invoice date
    const projectName = projectData.projectTitle || projectIdentifier || 'Project';
    const reportDate = formatFilenameDate(currentInvoice.invoice.invoiceDate);
    const reportNum = projectData.siteVisitNumber || '';
    const fileName = `CDCC INVOICE ${projectName}${reportDate ? ' ' + reportDate : ''}${reportNum ? ' Report ' + reportNum : ''}.pdf`;

    // Generate PDF and download
    pdfMake.createPdf(pdfDefinition as any).download(fileName);

    if (!suppressToast) {
      toast.success('All invoices PDF exported successfully!');
    }
  } catch (error) {
    console.error('PDF generation error:', error);
    if (suppressToast) {
      throw error;
    }
    toast.error('Failed to generate PDF');
  }
};
